import { PlusIcon } from '@langchain/untitled-ui-icons';

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { GraphConnectForm } from '@/Pages/Graph/GraphConnectPopover';
import { useGraphConnection } from '@/Pages/Graph/src/api/useGraphSwr';
import { STUDIO_HEADERS_LOCAL_STORAGE_KEY } from '@/Pages/Graph/src/constants';
import { SetupEvaluation } from '@/Pages/Onboarding/components/SetupEvaluation';
import { Pane } from '@/components/Pane';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Button, Text } from '@/design-system';
import { useOrganizationId } from '@/hooks/useSwr';
import { addQueryParams } from '@/utils/add-query-params';
import { appOrganizationPath, appPlaygroundPath } from '@/utils/constants';
import { useLocalStorageState } from '@/utils/use-local-storage-state';
import { useGetGraphStudioPath } from '@/utils/useGetGraphStudioPath';

type DropdownOption = {
  title: string;
  onSelect?: () => void;
  subMenuContent?: React.ReactNode;
};

export function NewExperimentButton({ datasetId }: { datasetId: string }) {
  const organizationId = useOrganizationId();
  const navigate = useNavigate();

  const [isNewEvalOpen, setIsNewEvalOpen] = useState(false);

  const playgroundPath = addQueryParams(
    `/${appOrganizationPath}/${organizationId}/${appPlaygroundPath}`,
    { datasetId: datasetId }
  );

  const { generatePath, lastStudioUrl, setLastStudioUrl } =
    useGetGraphStudioPath();
  const { error: lastActiveError, data: lastActive } = useGraphConnection({
    studioUrl: lastStudioUrl,
  });

  const [localHeaders, setLocalHeaders] = useLocalStorageState(
    STUDIO_HEADERS_LOCAL_STORAGE_KEY,
    {},
    z.record(z.string(), z.string())
  );
  const [baseUrl, setBaseUrl] = useState(lastStudioUrl);

  const addExampleDropdownOptions: DropdownOption[] = useMemo(() => {
    const handleNavigation = (path: string) => {
      navigate(path);
    };

    return [
      {
        title: 'Run in Playground',
        onSelect: () => handleNavigation(playgroundPath),
      },
      {
        title: 'Run in LangGraph Studio',
        subMenuContent: (
          <GraphConnectForm
            defaultHeaders={localHeaders}
            baseUrl={baseUrl}
            setBaseUrl={setBaseUrl}
            lastStudioUrl={lastStudioUrl}
            setLastStudioUrl={setLastStudioUrl}
            onSubmit={({ headers, hostProjectId }) => {
              setLastStudioUrl(baseUrl);
              if (headers) {
                setLocalHeaders(headers);
              }
              navigate(generatePath({ datasetId, hostProjectId }));
            }}
            connected={!!lastActive}
            connectionError={!!lastActiveError}
            showTabs={true}
          />
        ),
      },
      {
        title: 'Run in SDK',
        onSelect: () => setIsNewEvalOpen(true),
      },
    ];
  }, [
    navigate,
    playgroundPath,
    lastStudioUrl,
    setLastStudioUrl,
    localHeaders,
    baseUrl,
    lastActive,
    lastActiveError,
    setLocalHeaders,
    datasetId,
    generatePath,
  ]);

  const optionButton = useCallback((option: DropdownOption) => {
    return (
      <Button
        color="secondary"
        variant="plain"
        className="w-full justify-start"
        size="md"
        onClick={option.onSelect}
      >
        <Text variant="sm" weight="semibold">
          {option.title}
        </Text>
      </Button>
    );
  }, []);

  return (
    <>
      <Popover>
        <PopoverTrigger>
          <Button leftDecorator={PlusIcon} color="primary" size="md">
            Experiment
          </Button>
        </PopoverTrigger>
        <PopoverContent className="h-fit px-1.5 py-2">
          <div className="flex flex-col">
            {addExampleDropdownOptions.map((option) =>
              option.subMenuContent ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <div className="w-full">{optionButton(option)}</div>
                  </PopoverTrigger>
                  <PopoverContent
                    side="left"
                    align="start"
                    sideOffset={8}
                    className="w-[400px] p-1"
                  >
                    {option.subMenuContent}
                  </PopoverContent>
                </Popover>
              ) : (
                <div key={option.title} className="w-full">
                  {optionButton(option)}
                </div>
              )
            )}
          </div>
        </PopoverContent>
      </Popover>
      <Pane
        title="Setup Evaluation"
        open={isNewEvalOpen}
        onClose={() => setIsNewEvalOpen(false)}
      >
        <SetupEvaluation datasetId={datasetId} />
      </Pane>
    </>
  );
}
